<script lang="ts" setup>
import type { Component } from 'vue'
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'

import { useEntityStore } from '@/stores/entity'
import { useLayouts } from '@layouts'
import { VerticalNavGroup, VerticalNavLink, VerticalNavSectionTitle } from '@layouts/components'
import { config } from '@layouts/config'
import { injectionKeyIsVerticalNavHovered } from '@layouts/symbols'
import type { NavGroup, NavLink, NavSectionTitle, VerticalNavItems } from '@layouts/types'

import { VNodeRenderer } from './VNodeRenderer'

interface Props {
  tag?: string | Component
  navItems: VerticalNavItems
  isOverlayNavActive: boolean
  toggleIsOverlayNavActive: (value: boolean) => void
}

const props = withDefaults(defineProps<Props>(), {
  tag: 'aside',
})

const refNav = ref()
const refScrollbar = ref<{ ps?: unknown } | null>(null)

const { width: windowWidth } = useWindowSize()

const isHovered = useElementHover(refNav)

provide(injectionKeyIsVerticalNavHovered, isHovered)

const { isLessThanOverlayNavBreakpoint, isVerticalNavMini, isAppRtl } = useLayouts()

const hideTitleAndIcon = isVerticalNavMini(windowWidth, isHovered)
const isMini = isVerticalNavMini(windowWidth, isHovered)

function resolveNavItemComponent(item: NavLink | NavSectionTitle | NavGroup) {
  if ('heading' in item)
    return VerticalNavSectionTitle
  if ('children' in item)
    return VerticalNavGroup

  return VerticalNavLink
}

function showNavItem(item: NavLink | NavGroup) {
  if ('children' in item)
    return item.children.some(showNavItem)

  const entity = useEntityStore()
  if (entity.type && item.resource) {
    if ((item.resource).toLowerCase() === (entity.type || '').toLowerCase()) {
      if (item.flag)
        return import.meta.env[item.flag] === 'true'

      return true
    } else {
      return false
    }
  }

  return true
}

function filterItemsForFlags(array) {
  return array
    .filter(showNavItem)
    .map(item => {
      // If the item has children, apply the same filter to its children
      if (item.children && Array.isArray(item.children))
        return { ...item, children: filterItemsForFlags(item.children) }

      return item
    })
}

const filteredNavItems = computed(() => {
  return filterItemsForFlags(props.navItems)
})

/*
  ℹ️ Close overlay side when route is changed
  Close overlay vertical nav when link is clicked
*/
const route = useRoute()

watch(() => route.name, () => {
  props.toggleIsOverlayNavActive(false)
})

const isVerticalNavScrolled = ref(false)
const updateIsVerticalNavScrolled = (val: boolean) => isVerticalNavScrolled.value = val

function handleNavScroll(evt: Event) {
  isVerticalNavScrolled.value = (evt.target as HTMLElement).scrollTop > 0
}

/*
  When expanded, a long group (e.g. Customer Ops) can be scrolled far down.
  On collapse the children close but scrollTop is preserved, so the icon rail
  sits below the viewport and looks empty. Snap back to the top whenever we
  enter mini (collapsed + not hovered). nextTick waits for groups to close
  first so PerfectScrollbar can remeasure the shorter content.
*/
watch(isMini, mini => {
  if (!mini)
    return

  nextTick(() => {
    // vue3-perfect-scrollbar exposes `ps` as a ref; template refs unwrap it,
    // but unref keeps this safe either way.
    const ps = unref(refScrollbar.value?.ps as object | null | undefined) as {
      element?: HTMLElement
      update?: () => void
    } | null

    if (ps?.element) {
      ps.element.scrollTop = 0
      ps.update?.()
    }
    isVerticalNavScrolled.value = false
  })
})
</script>

<template>
  <Component
    :is="props.tag"
    ref="refNav"
    class="layout-vertical-nav"
    :class="[
      {
        'overlay-nav': isLessThanOverlayNavBreakpoint(windowWidth),
        'hovered': isHovered,
        'visible': isOverlayNavActive,
        'scrolled': isVerticalNavScrolled,
      },
    ]"
  >
    <!-- 👉 Header -->
    <div class="nav-header">
      <slot name="nav-header">
        <RouterLink
          to="/"
          class="app-logo d-flex align-center gap-x-3 app-title-wrapper"
        >
          <VNodeRenderer :nodes="config.app.logo" />

          <Transition name="vertical-nav-app-title">
            <h1
              v-show="!hideTitleAndIcon"
              class="font-weight-medium leading-normal text-xl text-uppercase"
            />
          </Transition>
        </RouterLink>
        <!-- 👉 Vertical nav actions -->
        <!-- Close button only, for overlay/mobile nav. No manual pin/collapse
             toggle — the nav is always collapsed at rest and expands on hover. -->
        <template v-if="isLessThanOverlayNavBreakpoint(windowWidth)">
          <Component
            :is="config.app.iconRenderer || 'div'"
            class="header-action"
            v-bind="config.icons.close"
            @click="toggleIsOverlayNavActive(false)"
          />
        </template>
      </slot>
    </div>
    <slot name="before-nav-items">
      <div class="vertical-nav-items-shadow" />
    </slot>
    <slot
      name="nav-items"
      :update-is-vertical-nav-scrolled="updateIsVerticalNavScrolled"
    >
      <PerfectScrollbar
        ref="refScrollbar"
        :key="isAppRtl"
        tag="ul"
        class="nav-items"
        :options="{ wheelPropagation: false }"
        @ps-scroll-y="handleNavScroll"
      >
        <Component
          :is="resolveNavItemComponent(item)"
          v-for="(item, index) in filteredNavItems"
          :key="index"
          :item="item"
        />
      </PerfectScrollbar>
    </slot>
  </Component>
</template>

<style lang="scss">
@use "@configured-variables" as variables;
@use "@layouts/styles/mixins";

// 👉 Vertical Nav
.layout-vertical-nav {
  position: fixed;
  z-index: variables.$layout-vertical-nav-z-index;
  display: flex;
  flex-direction: column;
  block-size: calc(100% - #{variables.$layout-vertical-nav-navbar-height});
  inline-size: variables.$layout-vertical-nav-width;
  inset-block-start: variables.$layout-vertical-nav-navbar-height;
  inset-inline-start: 0;
  transition: transform 0.25s ease-in-out, inline-size 0.25s ease-in-out, box-shadow 0.25s ease-in-out;
  will-change: transform, inline-size;

  // The top navbar now covers this row (logo lives there instead), so the
  // sidebar's own header is collapsed to nothing — nav items start right away.
  .nav-header {
    display: none;
  }

  .app-title-wrapper {
    margin-inline-end: auto;
  }

  // The logo SVG has its own hardcoded width/height attributes (matching its
  // native viewBox), which take precedence over the wrapping div's inline
  // size unless overridden here — without this the logo renders at native
  // size and gets clipped to a fragment by .nav-header's overflow: hidden.
  .app-logo svg {
    block-size: 100%;
    inline-size: 100%;
  }

  // Nav icons were reading too small at the default 24px.
  .nav-item-icon {
    block-size: 28px !important;
    inline-size: 28px !important;
  }

  // Sub-items have no leading icon — indent their text so it lines up with
  // the parent item's title. Must include the open-state top-level
  // margin-inline (0.75rem) + padding-start + icon + icon/text gap.
  .nav-group-children > .nav-link > a,
  .nav-group-children > .nav-group > .nav-group-label {
    padding-inline-start: calc(0.75rem + 1.375rem + 28px + 1rem) !important;
  }

  // The base template's default active-link treatment (a shared placeholder
  // extended onto exact-active links elsewhere in the theme layer) is a solid
  // purple fill + white text + drop shadow. Neutralized here in favor of the
  // two lighter treatments below — !important throughout because that rule
  // otherwise wins.

  // Top-level active item (Home, or a group's own label when any child route
  // is active): a light-purple 4px rectangle.
  // Use the `background` shorthand so we clear the template's purple
  // `linear-gradient(...) !important` active treatment.
  // Collapsed (icon-rail) mode overrides this below with a purple circle.
  // Do NOT set margin here — that would shift a parent left when a child is
  // selected (group gets .active). Equal side inset lives in the open rule.
  .nav-items > .nav-link > a.router-link-active,
  .nav-items > .nav-link > a.router-link-exact-active,
  .nav-items > .nav-group.active > .nav-group-label {
    border-radius: 4px !important;
    background: rgb(var(--v-theme-primary), 0.12) !important;
    border-end-end-radius: 4px !important;
    border-end-start-radius: 4px !important;
    border-start-end-radius: 4px !important;
    border-start-start-radius: 4px !important;
    box-shadow: none !important;
    color: rgb(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  }

  // Open/expanded only: 4px rectangle + equal left/right inset on top-level
  // items (and 4px rectangle on sub-items). Scoped to .hovered / .overlay-nav
  // so the collapsed icon rail is untouched.
  &.hovered,
  &.overlay-nav {
    .nav-items > .nav-link > a,
    .nav-items > .nav-group > .nav-group-label {
      border-radius: 4px !important;
      border-end-end-radius: 4px !important;
      border-end-start-radius: 4px !important;
      border-start-end-radius: 4px !important;
      border-start-start-radius: 4px !important;
      margin-inline: 0.75rem !important;
    }

    // More breathing room between the icon and label when expanded.
    .nav-items > .nav-link > a .nav-item-icon,
    .nav-items > .nav-group > .nav-group-label .nav-item-icon {
      margin-inline-end: 1rem !important;
    }

    .nav-group-children .nav-link > a,
    .nav-group-children .nav-group > .nav-group-label {
      border-radius: 4px !important;
      border-end-end-radius: 4px !important;
      border-end-start-radius: 4px !important;
      border-start-end-radius: 4px !important;
      border-start-start-radius: 4px !important;
    }
  }

  // Sub-item (dropdown child) active state: no fill at all — just a light-grey
  // underline under the text. Use the `background` shorthand (not just
  // `background-color`) so we also clear the template's purple
  // `linear-gradient(...) !important` active treatment.
  .nav-group-children .nav-link > a.router-link-active,
  .nav-group-children .nav-link > a.router-link-exact-active {
    border-radius: 4px !important;
    background: transparent !important;
    border-end-end-radius: 4px !important;
    border-end-start-radius: 4px !important;
    border-start-end-radius: 4px !important;
    border-start-start-radius: 4px !important;
    box-shadow: none !important;
    color: rgb(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
    text-decoration: underline !important;
    text-decoration-color: rgb(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity)) !important;
    text-underline-offset: 4px;
  }

  .nav-items {
    block-size: 100%;

    // Space between the top navbar and the first nav icons — the sidebar
    // header is hidden (logo lives in the navbar), so without this the icons
    // sit flush against the navbar edge.
    padding-block-start: 1.25rem;

    // ℹ️ We no loner needs this overflow styles as perfect scrollbar applies it
    // overflow-x: hidden;

    // // ℹ️ We used `overflow-y` instead of `overflow` to mitigate overflow x. Revert back if any issue found.
    // overflow-y: auto;
  }

  .nav-item-title {
    overflow: hidden;
    margin-inline-end: auto;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // 👉 Collapsed (icon rail — not hovered)
  .layout-vertical-nav-collapsed & {
    &:not(.hovered) {
      inline-size: variables.$layout-vertical-nav-collapsed-width;

      // Every top-level item uses the same 2.75rem centered box as the
      // selected circle. Unselected links otherwise keep expanded padding
      // (1.375rem start / 1rem end) plus the icon's title-gap, so the
      // glyph sits left of the rail center. All four radii need
      // !important to beat the template's asymmetric pill shape.
      .nav-items > .nav-link > a,
      .nav-items > .nav-group > .nav-group-label {
        justify-content: center !important;
        padding: 0 !important;
        border-radius: 50% !important;
        block-size: 2.75rem !important;
        border-end-end-radius: 50% !important;
        border-end-start-radius: 50% !important;
        border-start-end-radius: 50% !important;
        border-start-start-radius: 50% !important;
        inline-size: 2.75rem !important;
        margin-inline: auto !important;

        .nav-item-icon {
          margin-inline-end: 0 !important;
        }
      }

      // Active parent: light-purple circle around the icon, primary purple icon.
      .nav-items > .nav-link > a.router-link-active,
      .nav-items > .nav-link > a.router-link-exact-active,
      .nav-items > .nav-group.active > .nav-group-label {
        background: rgb(var(--v-theme-primary), 0.12) !important;
        box-shadow: none !important;
        color: rgb(var(--v-theme-primary)) !important;

        .nav-item-icon {
          color: rgb(var(--v-theme-primary)) !important;
        }
      }
    }
  }

  // 👉 Overlay nav
  &.overlay-nav {
    &:not(.visible) {
      transform: translateX(-#{variables.$layout-vertical-nav-width});

      @include mixins.rtl {
        transform: translateX(variables.$layout-vertical-nav-width);
      }
    }
  }
}
</style>
